import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoClose } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { Drawer } from '@mantine/core';
import { PocketBaseService } from '@/services/pocketbase.service';
import { useAuthedUserStore } from '@/store/store';
import { setLanguage } from '../../utils/language';
import { AppLogo } from '../app-logo';

const pb = PocketBaseService.getInstance().getPocketBase();

function getInitials(name?: string | null): string {
  if (!name?.trim()) {
    return '?';
  }
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

function formatBadge(count: number): string | null {
  if (count <= 0) {
    return null;
  }
  return count > 99 ? '99+' : String(count);
}

export type MobileOptionsDrawerVariant = 'app' | 'landing';

interface MobileOptionsDrawerProps {
  opened: boolean;
  onClose: () => void;
  /** `landing` skips badge fetches and app-only "Mein Bereich" links. */
  variant?: MobileOptionsDrawerVariant;
}

export function MobileOptionsDrawer({
  opened,
  onClose,
  variant = 'app',
}: MobileOptionsDrawerProps) {
  const { t, i18n } = useTranslation(['translation', 'landing']);
  const navigate = useNavigate();
  const { clearAuthedUser, user } = useAuthedUserStore();
  const isLogged = pb.authStore.isValid;
  const isLanding = variant === 'landing';
  const activeLang = i18n.language.startsWith('de') ? 'de' : 'en';

  const recordName = (pb.authStore.record?.name as string | undefined) || user?.name || '';
  const identityVerified = Boolean(pb.authStore.record?.identity_verified);

  const [messagesBadge, setMessagesBadge] = useState(0);
  const [exchangesBadge, setExchangesBadge] = useState(0);

  useEffect(() => {
    if (!opened || !isLogged || isLanding) {
      return;
    }

    const userId = pb.authStore.record?.id;
    if (!userId) {
      return;
    }

    pb.collection('chat_notifications')
      .getFullList({ filter: `user_id="${userId}"` })
      .then((rows) => {
        const total = rows.reduce((sum, row) => sum + (row.unread_count ?? 0), 0);
        setMessagesBadge(total);
      })
      .catch(() => setMessagesBadge(0));

    pb.send('/api/my-exchanges', {
      method: 'GET',
      query: { limit: 1, offset: 0 },
    })
      .then((response: { totalPending?: number }) => {
        setExchangesBadge(response.totalPending ?? 0);
      })
      .catch(() => setExchangesBadge(0));
  }, [opened, isLogged, isLanding]);

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  const handleLogout = () => {
    onClose();
    pb.authStore.clear();
    clearAuthedUser();
    window.location.href = '/login';
  };

  const messagesBadgeLabel = formatBadge(messagesBadge);
  const exchangesBadgeLabel = formatBadge(exchangesBadge);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="min(100%, 22.5rem)"
      offset={8}
      radius="lg"
      withCloseButton={false}
      classNames={{
        root: 'mobile-options-drawer',
        content: 'mobile-options-drawer__content',
        body: 'mobile-options-drawer__body',
        overlay: 'mobile-options-drawer__overlay',
      }}
      transitionProps={{ transition: 'slide-left', duration: 260 }}
      aria-label={t('translation:app-header-burger-aria-label')}
    >
      <div className="mobile-options-drawer__inner">
        <header className="mobile-options-drawer__header">
          <div className="mobile-options-drawer__brand">
            <AppLogo variant="white" width={132} height={27} />
          </div>
          <button
            type="button"
            className="mobile-options-drawer__close"
            onClick={onClose}
            aria-label={t('translation:app-header-mobile-close-aria')}
          >
            <IoClose size={20} aria-hidden />
          </button>
        </header>

        {isLogged ? (
          <button
            type="button"
            className="mobile-options-drawer__profile"
            onClick={() => go('/my-profile')}
          >
            <span className="mobile-options-drawer__avatar" aria-hidden>
              {getInitials(recordName)}
            </span>
            <span className="mobile-options-drawer__profile-text">
              <span className="mobile-options-drawer__profile-name">
                {recordName || t('translation:app-header-navlink-my-profile')}
              </span>
              <span className="mobile-options-drawer__profile-link">
                {identityVerified
                  ? t('translation:app-header-mobile-view-profile-verified')
                  : t('translation:app-header-mobile-view-profile')}
              </span>
            </span>
          </button>
        ) : (
          <button
            type="button"
            className="mobile-options-drawer__profile mobile-options-drawer__profile--guest"
            onClick={() => go('/login')}
          >
            <span className="mobile-options-drawer__avatar" aria-hidden>
              ?
            </span>
            <span className="mobile-options-drawer__profile-text">
              <span className="mobile-options-drawer__profile-name">
                {t('translation:app-header-navlink-sign-in-up')}
              </span>
              <span className="mobile-options-drawer__profile-link">
                {t('translation:app-header-mobile-sign-in-hint')}
              </span>
            </span>
          </button>
        )}

        <nav
          className="mobile-options-drawer__nav"
          aria-label={t('translation:app-header-burger-aria-label')}
        >
          <section className="mobile-options-drawer__section">
            <h2 className="mobile-options-drawer__section-label">
              {t('translation:app-header-mobile-section-discover')}
            </h2>
            {isLanding ? (
              <>
                <button
                  type="button"
                  className="mobile-options-drawer__item"
                  onClick={() => go('/guide')}
                >
                  {t('landing:nav.how-to-swap')}
                </button>
                <button
                  type="button"
                  className="mobile-options-drawer__item"
                  onClick={() => go('/swaps')}
                >
                  {t('landing:nav.browse-homes')}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="mobile-options-drawer__item"
                  onClick={() => go('/swaps')}
                >
                  {t('translation:app-header-navlink-home')}
                </button>
                <button
                  type="button"
                  className="mobile-options-drawer__item"
                  onClick={() => go(isLogged ? '/my-posts' : '/swaps')}
                >
                  {t('translation:app-header-mobile-nav-swaps')}
                </button>
              </>
            )}
          </section>

          {!isLanding && isLogged && (
            <section className="mobile-options-drawer__section">
              <h2 className="mobile-options-drawer__section-label">
                {t('translation:app-header-mobile-section-my-area')}
              </h2>
              <button
                type="button"
                className="mobile-options-drawer__item"
                onClick={() => go('/my-residence')}
              >
                {t('translation:app-header-mobile-nav-residence')}
              </button>
              <button
                type="button"
                className="mobile-options-drawer__item"
                onClick={() => go('/my-swaps')}
              >
                <span>{t('translation:app-header-navlink-my-exchanges')}</span>
                {exchangesBadgeLabel && (
                  <span className="mobile-options-drawer__badge">{exchangesBadgeLabel}</span>
                )}
              </button>
              <button
                type="button"
                className="mobile-options-drawer__item"
                onClick={() => go('/my-swaps')}
              >
                <span>{t('translation:app-header-navlink-messages')}</span>
                {messagesBadgeLabel && (
                  <span className="mobile-options-drawer__badge">{messagesBadgeLabel}</span>
                )}
              </button>
            </section>
          )}
        </nav>

        <div className="mobile-options-drawer__footer">
          <div className="mobile-options-drawer__language-row">
            <span className="mobile-options-drawer__language-label">
              {t('translation:language-switch-label-languages')}
            </span>
            <div
              className="mobile-options-drawer__language-pill"
              role="group"
              aria-label={t('translation:language-switch-aria-label')}
            >
              <button
                type="button"
                className={`mobile-options-drawer__language-option ${
                  activeLang === 'de' ? 'is-active' : ''
                }`}
                onClick={() => setLanguage('de')}
                aria-pressed={activeLang === 'de'}
              >
                DE
              </button>
              <button
                type="button"
                className={`mobile-options-drawer__language-option ${
                  activeLang === 'en' ? 'is-active' : ''
                }`}
                onClick={() => setLanguage('en')}
                aria-pressed={activeLang === 'en'}
              >
                EN
              </button>
            </div>
          </div>

          <div className="mobile-options-drawer__footer-links">
            <button
              type="button"
              className="mobile-options-drawer__footer-link"
              onClick={() => go('/contact')}
            >
              {t('translation:app-header-mobile-help-contact')}
            </button>
            {isLogged ? (
              <button
                type="button"
                className="mobile-options-drawer__footer-link is-muted"
                onClick={handleLogout}
              >
                {t('translation:app-header-navlink-logout')}
              </button>
            ) : (
              <button
                type="button"
                className="mobile-options-drawer__footer-link is-muted"
                onClick={() => go('/login')}
              >
                {t('translation:app-header-navlink-sign-in-up')}
              </button>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
}
